import doctorClinicMismatchPromptStore from "../../stores/DoctorClinicJoin/doctorClinicMismatchPromptStore";

/**
 * Save-time guard: is this prescription's doctor actually registered at the
 * practice it names?
 *
 * An Rx carries LANR and BSNR as two independent fields, and the printed stamp
 * takes its contact details - the phone number above all - from the PRACTICE.
 * A prescription naming a practice the doctor does not belong to therefore
 * leaves the building advertising a stranger's telephone number.
 *
 * A check already exists on the Arzt-Nr field, but it only runs when that field
 * loses focus and a practice is already set, so whether a prescription gets
 * validated depends on which field the user happened to touch last: correcting
 * the BSNR afterwards, or re-saving an Rx loaded from the database, both slip
 * past it. Hooking the check to the SAVE makes it independent of focus order.
 *
 * Resolves `true` when it is safe to save (the pair matches, or one of the two
 * is missing, or the user chose to save anyway) and `false` when the user wants
 * to go back and correct it. Deliberately a question rather than a block: a
 * doctor may genuinely work at a practice nobody has assigned yet, and
 * reception must stay able to record the prescription as written.
 */
export function confirmDoctorClinicMismatch(
  doctor: any,
  clinic: any,
): Promise<boolean> {
  // Nothing to compare - an Rx without one of the two is a separate concern
  // (and is allowed: prescriptions are often entered before the doctor is known).
  if (!doctor?.id || !clinic?.id) return Promise.resolve(true);

  // The doctor's practices come from the doctor record itself. An empty list
  // means "not loaded", not "belongs nowhere", so stay silent rather than
  // interrogate the user about something we cannot actually confirm.
  const clinics = doctor.clinics;
  if (!Array.isArray(clinics) || clinics.length === 0) return Promise.resolve(true);

  const belongsTogether = clinics.some(
    (c: any) => String(c?.id ?? "") === String(clinic.id),
  );
  if (belongsTogether) return Promise.resolve(true);

  const c = doctor.contactInfo || {};
  const doctorName = [c.salutation, c.firstNames, c.lastName].filter(Boolean).join(" ").trim();

  return doctorClinicMismatchPromptStore.requestConfirm({
    doctorName,
    doctorLANR: doctor.LANR || "",
    clinicName: clinic.contactInfo?.businessName || "",
    clinicBSNR: clinic.BSNR || "",
  });
}
